import React from "react";
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const TYPE_STYLES = {
  event: "border-blue-100 bg-blue-50 text-blue-800",
  birthday: "border-rose-100 bg-rose-50 text-rose-700",
  commemorative: "border-amber-100 bg-amber-50 text-amber-700",
};

export default function AgendaInlineCalendar({
  weeks = [],
  monthLabel = "",
  onPrevious,
  onNext,
  onToday,
  onOpenFull,
}) {
  const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  return (
    <div className="rounded-3xl border border-blue-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
            <FaCalendarAlt />
          </span>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-blue-500">
              Calendário visual
            </p>
            <h3 className="text-base font-semibold capitalize text-slate-900">
              {monthLabel}
            </h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPrevious}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-50"
            aria-label="Mês anterior"
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="rounded-full border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={onNext}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-50"
            aria-label="Próximo mês"
          >
            <FaChevronRight />
          </button>
          {onOpenFull && (
            <button
              type="button"
              onClick={onOpenFull}
              className="rounded-full bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
            >
              Ver completo
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto pb-1">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-7 gap-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            {weekdays.map((weekday) => (
              <div key={weekday} className="py-1">
                {weekday}
              </div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-2">
            {weeks.flatMap((week, weekIndex) =>
              week.map((day, dayIndex) => {
                const visibleItems = (day.items || []).slice(0, 3);
                const hiddenCount = Math.max(0, (day.items || []).length - visibleItems.length);
                return (
                  <div
                    key={`${day.key || day.date?.toISOString?.() || "day"}-${weekIndex}-${dayIndex}`}
                    className={[
                      "min-h-[104px] rounded-2xl border p-2 transition",
                      day.inMonth
                        ? "border-slate-200 bg-white"
                        : "border-slate-100 bg-slate-50/80 text-slate-400",
                      day.isToday ? "ring-2 ring-blue-200 border-blue-400" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={[
                          "inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-xs font-semibold",
                          day.isToday
                            ? "bg-blue-600 text-white"
                            : day.inMonth
                              ? "text-slate-700"
                              : "text-slate-400",
                        ].join(" ")}
                      >
                        {day.date?.getDate?.() ?? ""}
                      </span>
                      {(day.items || []).length > 0 && (
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-500">
                          {(day.items || []).length}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {visibleItems.map((item, itemIndex) => (
                        <div
                          key={`${item.type || "item"}-${item.label || itemIndex}-${itemIndex}`}
                          className={`truncate rounded-lg border px-1.5 py-1 text-[10px] font-medium ${TYPE_STYLES[item.type] || "border-slate-100 bg-slate-50 text-slate-700"}`}
                          title={item.label}
                        >
                          {item.timeLabel ? `${item.timeLabel} · ` : ""}
                          {item.label}
                        </div>
                      ))}
                      {hiddenCount > 0 && (
                        <div className="px-1 text-[9px] font-semibold text-slate-400">
                          +{hiddenCount} evento{hiddenCount === 1 ? "" : "s"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
